"use client";

import React, { useEffect, useState } from "react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { ProductsSearch } from "@/components/shop/ProductsSearch";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MapPin, ChevronDown, User, Mail, BookOpen, Globe, Search, Menu } from "lucide-react";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-4">
        {[...Array(5)].map((_, i) => <div key={i} data-index={i} className="h-6 w-20 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-sm font-semibold text-foreground hover:text-primary transition-colors">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="p-4 text-sm text-muted-foreground">Cargando menú...</div>;
  }
  return <div className="flex flex-col gap-1 p-2">
      {menuItems.map((item, i) => <DropdownMenuItem key={item.id} data-index={i} asChild>
          <Link to={item.href || "#"} className="w-full cursor-pointer text-sm font-medium">
            {item.label}
          </Link>
        </DropdownMenuItem>)}
    </div>;
}
function SearchPopover() {
  const [searchValue, setSearchValue] = useState("");
  return <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="text-foreground hover:text-primary">
          <Search className="size-5" />
          <span className="sr-only">Buscar productos</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-4">
        <div className="space-y-2">
          <h4 className="font-medium text-sm text-foreground">Buscar</h4>
          <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Buscar productos..." />
        </div>
      </PopoverContent>
    </Popover>;
}
export default function MultiTierNavigationHeader() {
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="multitiernavigationheader" className="sticky top-0 z-50 w-full bg-background shadow-sm">
      {}
      <div className="hidden lg:flex justify-between items-center py-1.5 px-6 xl:px-12 border-b border-border bg-muted/30">
        {}
        <div className="flex items-center gap-2 cursor-pointer group">
          <MapPin className="size-5 text-primary group-hover:text-primary/80 transition-colors" />
          <div className="flex flex-col leading-none">
            <span className="text-sm font-bold text-foreground group-hover:text-primary transition-colors">
              Colegio Militar
            </span>
            <span className="text-[11px] text-muted-foreground mt-0.5">
              8:00 AM - 5:30 PM
            </span>
          </div>
          <ChevronDown className="size-4 text-muted-foreground ml-1 group-hover:text-primary transition-colors" />
        </div>

        {}
        <div className="flex items-center gap-6 text-sm font-medium text-muted-foreground">
          <Link to="#" className="flex items-center gap-1.5 hover:text-primary transition-colors">
            <User className="size-4 text-primary" />
            Cliente Consentido
          </Link>
          <Link to="#industries-served" className="flex items-center gap-1.5 hover:text-primary transition-colors">
            <MapPin className="size-4 text-primary" />
            Sucursales
          </Link>
          <Link to="#" className="flex items-center gap-1.5 hover:text-primary transition-colors">
            <Mail className="size-4 text-primary" />
            Contacto
          </Link>
          <Link to="#" className="flex items-center gap-1.5 hover:text-primary transition-colors">
            <BookOpen className="size-4 text-primary" />
            Catálogo
          </Link>
          <Link to="/" className="flex items-center gap-1.5 hover:text-primary transition-colors">
            <Globe className="size-4 text-primary" />
            English
          </Link>
        </div>
      </div>

      {}
      <div className="flex justify-between items-center py-4 px-4 md:px-6 xl:px-12">
        {}
        <Link to="/" className="flex-shrink-0 mr-8">
          <WvcLogo className="h-10 w-auto" />
        </Link>

        {}
        <div className="hidden lg:flex items-center flex-1 justify-end gap-8">
          <Button onClick={() => window.open('https://wa.me/526149800517', '_blank')} className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold px-8 rounded-md shadow-sm">
            Cotizar
          </Button>
          
          <MenuProvider menu_id="45">
            <HeaderMenuConsumer />
          </MenuProvider>

          <SearchPopover />
        </div>

        {}
        <div className="flex items-center gap-4 lg:hidden">
          <SearchPopover />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="text-foreground">
                <Menu className="size-6" />
                <span className="sr-only">Abrir menú</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 p-2">
              <div className="mb-4 p-2">
                <Button onClick={() => window.open('https://wa.me/526149800517', '_blank')} className="w-full bg-primary text-primary-foreground font-bold rounded-md">
                  Cotizar
                </Button>
              </div>
              <MenuProvider menu_id="45">
                <MobileMenuConsumer />
              </MenuProvider>
              <div className="mt-4 border-t border-border pt-4 p-2 flex flex-col gap-3 text-sm">
                <Link to="#" className="flex items-center gap-2 text-muted-foreground hover:text-primary">
                  <User className="size-4" /> Cliente Consentido
                </Link>
                <Link to="#industries-served" className="flex items-center gap-2 text-muted-foreground hover:text-primary">
                  <MapPin className="size-4" /> Sucursales
                </Link>
                <Link to="#" className="flex items-center gap-2 text-muted-foreground hover:text-primary">
                  <Mail className="size-4" /> Contacto
                </Link>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </section>;
}